/**
 * AtlasFaunaModule — the birds and the forest's insects (2026-09-11, "bug
 * particles that can interact with your user in a forest, birds that can fly
 * around, land on trees, land on your parked ship and fly through cities,
 * towns and migrate … land on the ground to act like they are looking for
 * food or drinking water from a lake or pond, if a versona or user gets close
 * to them they fly off … well detailed … a few different types").
 *
 * The RULES are pure (lib/birds, lib/bugs); this module owns the world:
 *   PERCHES   trees from the forest field (the same lattice the planter uses),
 *             rooftops from the structure-solids registry, the PARKED SHIPS
 *             (the vehicle module's berths), open ground and SHORE points
 *             (where a dry sample meets water — sea, lake or river).
 *   THREATS   the viewer, other travelers, walking versonas, creatures.
 *   BODIES    per species TWO instanced meshes — the FLYING model (wings
 *             spread; a TSL hinge flaps the wing tips by a per-instance
 *             phase, with the previous phase for the motion vectors) and the
 *             PERCHED model (wings folded) — from the generated GLBs
 *             (`prop:bird_<species>_<pose>` in the prefab catalog, cooked by
 *             tools/asset-pipeline/gen-fauna.mjs) or a procedural stand-in
 *             until they land (self-heal: the catalog is re-read every few
 *             seconds).
 *   SKEINS    migrating flocks in a V crossing the viewer's sky high up.
 *   BUGS      one swarm around the viewer inside the woods — gnats by day,
 *             moths at dusk, fireflies at night — instanced camera-facing
 *             dots that keep a bubble around every person and drift to a
 *             still viewer's head.
 *   SOUNDS    calls per species when settled, a flutter on a flush, a flock
 *             passing overhead, the gnats' hum near the head.
 * Everything is ambient and client-side (like the weather); instancers are
 * scene children at ABSOLUTE coordinates (the FP64 rebase law).
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { Fn, attribute, positionLocal, positionGeometry, positionPrevious, sin, abs, max, mix, float, vec3, uv, length, smoothstep, uniform, color } from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { App, IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import type { WeatherModule } from './WeatherModule.js';
import type { WildsModule } from './WildsModule.js';
import type { AtlasVehicleModule } from './AtlasVehicleModule.js';
import type { AssetModule } from './AssetModule.js';
import { uSunDir } from './SkyModule.js';
import { windVector } from '../lib/wind.js';
import { nightFactor } from '../lib/lighting.js';
import { forestDensity, forestTreesForChunk } from '../lib/forest_field.js';
import { commonsSeaLevel } from '../lib/sea_level.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { PLATEAU_RADIUS } from '../../../shared/terrain.js';
import { structureSolidsNear, structureHitAt } from '../lib/structure_solids.js';
import { waterStillAt } from '../lib/water_fx.js';
import { lakeSurfaceAtBaked } from '../lib/lakes.js';
import { sfxAt, sfxAvailable, sfxLater, sfxLoop } from '../lib/sfx.js';
import { activePreset, activeTier } from '../lib/quality_tiers.js';
import { CHUNK_SIZE } from '../config.js';
import { deferDisposeTree } from '../lib/defer_dispose.js';
import {
  BIRD_SPECIES, BIRD_SPECIES_KEYS, BIRD_RANGE_M, BIRD_DESPAWN_M, BIRD_MAX, SKEIN_SIZE, habitatFor, populationFor, makeBird, steerToward,
  arriveSpeed, flockSlot, shouldFlee, fleeTarget, pickPerchKind, landedState, dwellFor, groundHop, flapAdvance, soarTarget,
  planSkein, skeinSlot, randRange, ARRIVE_M, FLEE_BURST_S, FLEE_SPEED_MULT, SKEIN_EVERY_S,
  type Bird, type BirdSpeciesKey, type Target, type PerchKind, type Habitat, type Skein, type BirdRng,
} from '../lib/birds.js';
import { BUG_RULES, bugKindFor, swarmAllowed, stepBug, fireflyGlow, swarmCentreStep, type BugKind, type BugState, type Person } from '../lib/bugs.js';

type Pose = 'fly' | 'perch';
interface BirdModel {
  mesh: THREE.InstancedMesh;
  /** Per-instance (flap phase, previous phase) — ONE vec2 buffer (WebGPU allows 8 vertex buffers a pipeline). */
  flap: THREE.InstancedBufferAttribute;
  /** Model half wingspan (local units) and the model's own scale to a 1 m wingspan. */
  unit: number;
  /** Yaw the model needs to face −Z (the vehicle convention). */
  yawOff: number;
  /** Vertical offset so the perched model's feet sit on the perch (local, pre-scale). */
  feet: number;
  source: 'placeholder' | 'glb';
  release: (() => void) | null;
}
interface PerchSet { trees: Target[]; giants: Target[]; roofs: Target[]; ships: Target[]; ground: Target[]; shores: Array<Target & { waterY: number }> }

/** Which generated FLYING models are upright (head +Y, belly +Z) rather than horizontal (head +Z) — seen, not inferred. */
const FLY_UPRIGHT: Partial<Record<BirdSpeciesKey, boolean>> = { jay: true, starling: true, gull: false, hawk: false };
/** Birds per tier (a hovering flock is forty draw-less instances; the CPU steering is the cost). */
// Birds per tier. `minimal` used to be 0 — a WORLD WITH NO BIRDS AT ALL, which
// is a large atmosphere loss for a very small saving: birds are instanced (a
// handful of draws) and their cost is the per-bird FSM, not the GPU. A floor of
// 6 keeps the sky and the trees alive on the weakest hardware. NB auto-tune
// walks this ladder on its own, so a machine that drops to minimal used to lose
// its birds silently — `__fauna().tier` and `.cap` now say so (2026-09-14).
const BIRD_CAP: Record<string, number> = { minimal: 6, potato: 14, low: 24, med: 34, high: BIRD_MAX, ultra: BIRD_MAX };
const SEASONS = ['spring', 'summer', 'autumn', 'winter'] as const;
/** Calls: only birds inside this range are heard, and never two within the gap. */
const CALL_RANGE_M = 28;
const CALL_GAP_S = 7;
/** The viewer is ALOFT (a ship, fly mode) above this height over the ground: birds ride the sky around them. */
const ALOFT_AGL_M = 45;
const PERCH_REFRESH_S = 2;
/** Instancer keys per species × pose, built once (no `${k}_${pose}` per bird per frame). */
const MODEL_KEYS = Object.fromEntries(BIRD_SPECIES_KEYS.map((k) => [k, { fly: `${k}_fly`, perch: `${k}_perch` }])) as Record<BirdSpeciesKey, Record<Pose, string>>;
const THREAT_EVERY_S = 0.3;
const MODEL_RETRY_S = 6;

export class AtlasFaunaModule implements IModule {
  readonly name = 'Fauna';
  private app: App | null = null;
  private scene!: THREE.Scene;
  private env: EnvironmentModule | null = null;
  private weather: WeatherModule | null = null;
  private wilds: WildsModule | null = null;
  private vehicle: AtlasVehicleModule | null = null;
  private assets: AssetModule | null = null;
  private seed = '';
  private sea = 0;
  private sites: SettlementSite[] = [];
  private rngState = 0x9e3779b9;
  private readonly rng: BirdRng = () => { this.rngState = (this.rngState * 1664525 + 1013904223) >>> 0; return this.rngState / 4294967296; };
  // ── birds ──
  private birds: Bird[] = [];
  private nextId = 1;
  private models = new Map<string, BirdModel>();
  private modelRetryIn = 0;
  private perches: PerchSet = { trees: [], giants: [], roofs: [], ships: [], ground: [], shores: [] };
  private perchIn = 0;
  private threats: Array<{ x: number; y: number; z: number }> = [];
  private threatIn = 0;
  private habitat: Habitat = 'open';
  private night = 0;
  /** The viewer's height over the ground (a ship in flight, fly mode). */
  private viewerAgl = 0;
  private popIn = 0;
  private skeins: Skein[] = [];
  private skeinIn = 60;
  /** Why the tick short-circuited last frame (null = running) — see __fauna(). */
  private lastGate: string | null = null;
  private stats = { flees: 0, calls: 0, landings: { tree: 0, roof: 0, ship: 0, ground: 0, shore: 0 } as Record<string, number>, skeins: 0, flockSounds: 0, perchMs: 0, perchMaxMs: 0 };
  /** Per-frame scratch (2026-09-12 perf sweep): no Map / closure / string allocation in the render or step loops. */
  private readonly counts = new Map<string, number>();
  private readonly byId = new Map<number, Bird>();
  private readonly people: Person[] = [];
  private disposed = false;
  private lastFlutterMs = 0;
  // ── bugs ──
  private bugs: BugState[] = [];
  private bugKind: BugKind | null = null;
  private bugMesh: THREE.InstancedMesh | null = null;
  private bugGlow: THREE.InstancedBufferAttribute | null = null;
  private readonly uBugColor = uniform(new THREE.Color(0x2a2620));
  private readonly uBugEmissive = uniform(0);
  private swarmX = 0; private swarmZ = 0;
  private stillFor = 0; private lastPx = 0; private lastPz = 0;
  private bugNearHead = 0;
  // scratch
  private readonly _m = new THREE.Matrix4();
  private readonly _q = new THREE.Quaternion();
  private readonly _e = new THREE.Euler(0, 0, 0, 'YXZ');
  private readonly _p = new THREE.Vector3();
  private readonly _s = new THREE.Vector3();
  private readonly _camQ = new THREE.Quaternion();

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene as THREE.Scene;
    bus.on('atlas:world-entered', () => this.onWorld());
    bus.on('atlas:environment-updated', () => this.onWorld());
    app.world.addSystem(makeSystem('AtlasFauna', Phase.Animation, 40, (dt) => this.tick(dt)));
    (globalThis as unknown as Record<string, unknown>).__fauna = () => this.diag();
    (globalThis as unknown as Record<string, unknown>).__faunaSend = (kind: PerchKind = 'shore') => {
      // Probe lever: the nearest flying bird flies to a random perch of this kind (null when none exists).
      const pool = kind === 'tree' ? this.perches.trees : kind === 'roof' ? this.perches.roofs : kind === 'ship' ? this.perches.ships : kind === 'ground' ? this.perches.ground : this.perches.shores;
      if (!pool.length) return null;
      const px = atlas.localX, pz = atlas.localZ;
      let best: Bird | null = null, bd = Infinity;
      for (const b of this.birds) if (b.state === 'fly' || b.state === 'soar') { const d = Math.hypot(b.x - px, b.z - pz); if (d < bd) { bd = d; best = b; } }
      if (!best) return null;
      const t = pool[Math.floor(this.rng() * pool.length)];
      best.state = 'fly'; best.target = { ...t }; best.flock = best.id;
      return { id: best.id, target: best.target, from: { x: best.x, y: best.y, z: best.z } };
    };
    (globalThis as unknown as Record<string, unknown>).__faunaSkein = () => { const px = atlas.localX, pz = atlas.localZ; const s = planSkein(px, pz, this.flightFloor(px, pz), 'autumn', this.rng); this.skeins.push(s); this.stats.skeins++; return { species: s.species, count: s.count, alt: Math.round(s.y - this.groundAt(px, pz)), life: Math.round(s.life) }; };
    (globalThis as unknown as Record<string, unknown>).__faunaSpawnNear = (species: BirdSpeciesKey = 'jay', dist = 8, state: 'ground' | 'perch' = 'ground') => this.spawnNear(species, dist, state);
  }

  private onWorld(): void {
    const conn = atlas.conn;
    if (!conn) return;
    let seed = '';
    try { const id = atlas.currentWorldId; if (id !== null) { const w = conn.db.world.worldId.find(id); if (w && w.kind === 'commons') seed = w.terrainSeed; } } catch { /* mid-churn */ }
    if (seed === this.seed) return;
    this.clearBirds();
    this.seed = seed;
    if (!seed) { this.sites = []; return; }
    this.sea = commonsSeaLevel(seed);
    try { this.sites = settlementSites(seed); } catch { this.sites = []; }
    this.modelRetryIn = 0;
  }

  private mods(): boolean {
    const app = this.app;
    if (!app) return false;
    this.env ??= (app.getModule?.('Environment') as EnvironmentModule | undefined) ?? null;
    this.weather ??= (app.getModule?.('Weather') as WeatherModule | undefined) ?? null;
    this.wilds ??= (app.getModule?.('Wilds') as WildsModule | undefined) ?? null;
    this.vehicle ??= (app.getModule?.('AtlasVehicle') as AtlasVehicleModule | undefined) ?? null;
    this.assets ??= (app.getModule?.('AssetModule') as AssetModule | undefined) ?? null;
    return !!this.env;
  }

  private groundAt(x: number, z: number): number { return this.env ? this.env.groundAt(x, z) : 0; }
  /**
   * The floor a FLYING bird measures its altitude from: the water surface
   * over the sea, a lake or a river; the terrain otherwise. WHY: altitudes
   * were relative to the BED, so gulls over 30 m of sea targeted (and
   * spawned at) heights tens of metres under the surface (2026-09-12).
   */
  private flightFloor(x: number, z: number): number {
    const g = this.groundAt(x, z);
    const w = waterStillAt(x, z) ?? lakeSurfaceAtBaked(x, z);
    return w !== null && w > g ? w : g;
  }

  // ─── the tick ──────────────────────────────────────────────────────────────
  private seedRetryIn = 0;
  private lastTickMs = 0;
  private tick(dtRaw: number): void {
    if (!this.seed && atlas.currentWorldId !== null) {
      // The world row can land AFTER the entry event (a resume-reload subscribes
      // late): keep asking until the seed resolves, or the birds never come.
      this.seedRetryIn -= dtRaw;
      if (this.seedRetryIn <= 0) { this.seedRetryIn = 2; this.onWorld(); }
    }
    // Why the module might be idle — reported by __fauna().gate so "where are
    // the birds?" is one command rather than a bisect (2026-09-14).
    this.lastGate = !this.seed ? 'no world seed yet'
      : atlas.loadingGate ? 'loading gate is up'
        : atlas.currentWorldId !== atlas.commonsWorldId ? 'not in the Commons (personal world or space)'
          : atlas.spaceFlight ? 'in space flight'
            : null;
    if (this.lastGate) { if (this.birds.length) this.clearBirds(); this.clearBugs(); return; }
    if (!this.mods()) { this.lastGate = 'modules not resolved yet'; return; }
    // AMBIENT TIME (the WALL CLOCK, not the engine's dt): the engine clamps a
    // frame's delta to a few fixed steps, so under a slow renderer a 9 m/s
    // bird crawled at 0.17 m/s and a perch dwell of 25 s took an hour (the
    // 2026-09-11 probe trace). Birds and bugs are ambient — they keep real
    // time, up to 1 s a frame, integrated in ≤ 50 ms sub-steps so the
    // steering and the swarm stay stable.
    const nowMs = performance.now();
    const dtWall = Math.min(1.0, Math.max(0, this.lastTickMs ? (nowMs - this.lastTickMs) / 1000 : dtRaw));
    this.lastTickMs = nowMs;
    void dtRaw;
    const subs = Math.max(1, Math.ceil(dtWall / 0.05));
    const dt = dtWall / subs;
    const px = atlas.localX, pz = atlas.localZ, py = atlas.localY;
    this.night = nightFactor((uSunDir.value as THREE.Vector3).y);
    this.viewerAgl = atlas.spaceFlight ? 0 : py - this.groundAt(px, pz);
    this.modelRetryIn -= dtWall;
    if (this.modelRetryIn <= 0) { this.modelRetryIn = MODEL_RETRY_S; this.ensureModels(); }
    this.perchIn -= dtWall;
    if (this.perchIn <= 0) {
      this.perchIn = PERCH_REFRESH_S;
      const t0 = performance.now(); this.refreshPerches(px, pz);
      const ms = performance.now() - t0; this.stats.perchMs = ms; if (ms > this.stats.perchMaxMs) this.stats.perchMaxMs = ms;
    }
    this.threatIn -= dtWall;
    if (this.threatIn <= 0) { this.threatIn = THREAT_EVERY_S; this.refreshThreats(px, py, pz); }
    this.popIn -= dtWall;
    if (this.popIn <= 0) { this.popIn = 2; this.managePopulation(px, pz); }
    this.byId.clear(); for (const b of this.birds) this.byId.set(b.id, b); // the followers' leader lookup: O(1), not a find per follower per substep
    for (let s = 0; s < subs; s++) for (const b of this.birds) this.stepBird(b, dt, px, pz);
    this.stepSkeins(dtWall, px, pz);
    this.renderBirds();
    this.stepBugs(dtWall, subs, px, py, pz);
  }

  // ─── perches + threats + habitat ─────────────────────────────────────────
  private refreshPerches(px: number, pz: number): void {
    const R = BIRD_RANGE_M;
    const trees: Target[] = [], giants: Target[] = [];
    const cx0 = Math.floor((px - R) / CHUNK_SIZE), cx1 = Math.floor((px + R) / CHUNK_SIZE);
    const cz0 = Math.floor((pz - R) / CHUNK_SIZE), cz1 = Math.floor((pz + R) / CHUNK_SIZE);
    // NO TREES DRAWN ⇒ NO TREE PERCHES (2026-09-14). Perches come from the
    // seed-derived forest lattice, which exists at every tier; the rendered
    // trees do not. `foliageDensityScale: 0` (the minimal tier) disposes every
    // chunk's foliage, so a bird landing on a "tree" sat in empty air with
    // nothing under it. Skip the tree pools in that case and let the ground,
    // shore and roof pools take the landings.
    const treesDrawn = (activePreset().foliageDensityScale ?? 1) > 0;
    if (treesDrawn) for (let cx = cx0; cx <= cx1; cx++) for (let cz = cz0; cz <= cz1; cz++) {
      let list: ReturnType<typeof forestTreesForChunk> = [];
      try { list = forestTreesForChunk(this.seed, cx * CHUNK_SIZE, cz * CHUNK_SIZE, CHUNK_SIZE, this.sea, 1); } catch { list = []; }
      for (const t of list) {
        const d = Math.hypot(t.x - px, t.z - pz);
        if (d > R) continue;
        const y = this.groundAt(t.x, t.z) + t.targetH * 0.78;
        const target: Target = { x: t.x, y, z: t.z, kind: 'tree' };
        (t.cls === 'giant' ? giants : trees).push(target);
        if (trees.length > 120) break;
      }
    }
    const roofs: Target[] = [];
    for (const s of structureSolidsNear(px, pz, R)) {
      for (let i = 0; i < 2 && roofs.length < 60; i++) {
        const u = (this.rng() - 0.5) * 1.2, v = (this.rng() - 0.5) * 1.2;
        const lx = u * s.hx, lz = v * s.hz;
        roofs.push({ x: s.x + lx * Math.cos(s.yaw) - lz * Math.sin(s.yaw), y: s.top + 0.05, z: s.z + lx * Math.sin(s.yaw) + lz * Math.cos(s.yaw), kind: 'roof' });
      }
    }
    const ships: Target[] = [];
    for (const sh of this.vehicle?.parkedShips() ?? []) {
      if (Math.hypot(sh.x - px, sh.z - pz) > R) continue;
      for (let i = 0; i < 3; i++) ships.push({ x: sh.x + (this.rng() - 0.5) * sh.half * 0.9, y: sh.top, z: sh.z + (this.rng() - 0.5) * sh.half * 0.9, kind: 'ship' });
    }
    const ground: Target[] = [];
    for (let i = 0; i < 40 && ground.length < 14; i++) {
      const a = this.rng() * Math.PI * 2, d = 12 + this.rng() * 70;
      const x = px + Math.cos(a) * d, z = pz + Math.sin(a) * d;
      const y = this.groundAt(x, z);
      const lvl = waterStillAt(x, z) ?? lakeSurfaceAtBaked(x, z);
      if (lvl !== null && y < lvl - 0.15) continue; // under water
      if (structureHitAt(x, z, y + 1, 0)) continue;
      ground.push({ x, y, z, kind: 'ground' });
    }
    const shores: Array<Target & { waterY: number }> = [];
    for (let k = 0; k < 16 && shores.length < 12; k++) {
      const a = (k / 16) * Math.PI * 2;
      let dryX = px, dryZ = pz;
      // The WATERLINE: a sample is wet when the ground lies UNDER a water level
      // (the sea, a river, or a baked lake — whose box covers dry margins, so
      // membership alone is not water). The shore is the last dry sample
      // before a wet one, low enough to drink from.
      const wetAt = (x: number, z: number): number | null => {
        const w = waterStillAt(x, z) ?? lakeSurfaceAtBaked(x, z);
        return w !== null && this.groundAt(x, z) < w - 0.15 ? w : null;
      };
      let wasDry = wetAt(px, pz) === null;
      for (let d = 6; d <= R; d += 6) {
        const x = px + Math.cos(a) * d, z = pz + Math.sin(a) * d;
        const w = wetAt(x, z);
        if (w !== null && wasDry) { const gy = this.groundAt(dryX, dryZ); if (gy >= w - 0.3 && gy <= w + 2.5) shores.push({ x: dryX, y: gy, z: dryZ, kind: 'shore', waterY: w }); break; }
        wasDry = w === null;
        if (wasDry) { dryX = x; dryZ = z; }
      }
    }
    this.perches = { trees, giants, roofs, ships, ground, shores };
    // Habitat: the nearest settlement (inside 1.4× its plateau), water within reach, the woods.
    let tier: 'city' | 'town' | 'hamlet' | null = null;
    let best = Infinity;
    // Capital wave: a district or capital is city habitat, the circuit campus town habitat (the birds lib keeps the classic tiers).
    const classicTier = (t: SettlementSite['tier']): 'city' | 'town' | 'hamlet' => t === 'district' || t === 'capital' ? 'city' : t === 'circuit' ? 'town' : t;
    for (const s of this.sites) { const d = Math.hypot(s.centerX - px, s.centerZ - pz); if (d < PLATEAU_RADIUS[s.tier] * 1.4 && d < best) { best = d; tier = classicTier(s.tier); } }
    const nearWater = shores.some((s) => Math.hypot(s.x - px, s.z - pz) < 90);
    this.habitat = habitatFor(forestDensity(this.seed, px, pz, this.sea), nearWater, tier);
  }

  private refreshThreats(px: number, py: number, pz: number): void {
    const out: Array<{ x: number; y: number; z: number }> = [{ x: px, y: py, z: pz }];
    for (const r of atlas.remotes.values()) if (r.mesh) out.push({ x: r.mesh.position.x, y: r.mesh.position.y, z: r.mesh.position.z });
    if (this.env) for (const w of this.env.walkers(px, pz, BIRD_RANGE_M)) out.push({ x: w.x, y: w.y, z: w.z });
    if (this.wilds) for (const c of this.wilds.creaturePositions()) if (Math.hypot(c.x - px, c.z - pz) < BIRD_RANGE_M) out.push(c);
    this.threats = out;
  }

  private nearestThreat(x: number, z: number): number {
    let best = Infinity;
    for (const t of this.threats) { const d = Math.hypot(t.x - x, t.z - z); if (d < best) best = d; }
    return best;
  }

  // ─── population ──────────────────────────────────────────────────────────
  private managePopulation(px: number, pz: number): void {
    const cap = BIRD_CAP[activeTier()] ?? BIRD_MAX;
    // NEVER a bird vanishing in view: only birds far beyond the range are
    // recycled (a speck at 260 m), never one near the viewer.
    this.birds = this.birds.filter((b) => Math.hypot(b.x - px, b.z - pz) < BIRD_DESPAWN_M);
    if (cap <= 0) { this.birds = []; return; }
    const want = populationFor(this.habitat, this.night, cap);
    // ALOFT (a ship, fly mode): flocks ride the sky around the viewer — starlings
    // and gulls over cities and coasts, hawks over the woods — never landing
    // while the viewer is up there.
    if (this.viewerAgl > ALOFT_AGL_M) {
      const aloft = this.birds.filter((b) => b.aloft).length;
      const wantAloft = Math.min(cap - (this.birds.length - aloft), this.habitat === 'city' || this.habitat === 'town' ? 14 : this.habitat === 'coast' ? 10 : this.habitat === 'forest' ? 8 : 5);
      if (aloft < wantAloft - 2) this.spawnAloft(this.habitat === 'coast' ? 'gull' : this.habitat === 'forest' || this.habitat === 'open' ? (this.rng() < 0.5 ? 'hawk' : 'jay') : 'starling', wantAloft - aloft, px, pz);
    } else {
      for (const b of this.birds) if (b.aloft) { b.aloft = false; b.target = null; } // back to the perches once the viewer lands
    }
    const have: Record<BirdSpeciesKey, number> = { jay: 0, gull: 0, hawk: 0, starling: 0 };
    for (const b of this.birds) have[b.species]++;
    for (const k of BIRD_SPECIES_KEYS) {
      const deficit = want[k] - have[k];
      if (deficit >= 1 && this.birds.length < cap) this.spawnFlock(k, Math.min(deficit, cap - this.birds.length), px, pz);
      else if (deficit <= -3) {
        // Too many (night fell): the farthest flying one is sent AWAY (an air
        // target past the range — it leaves in view and is recycled out of it).
        let far: Bird | null = null, fd = 0;
        for (const b of this.birds) if (b.species === k && (b.state === 'fly' || b.state === 'soar') && !b.leaving) { const d = Math.hypot(b.x - px, b.z - pz); if (d > fd) { fd = d; far = b; } }
        if (far) { far.leaving = true; far.state = 'fly'; const a = Math.atan2(far.z - pz, far.x - px); far.target = { x: px + Math.cos(a) * (BIRD_DESPAWN_M + 40), y: far.y + 10, z: pz + Math.sin(a) * (BIRD_DESPAWN_M + 40), kind: 'air' }; far.timer = 60; }
      }
    }
  }

  private spawnFlock(k: BirdSpeciesKey, n: number, px: number, pz: number): void {
    const sp = BIRD_SPECIES[k];
    const size = Math.max(1, Math.min(n, Math.round(randRange(this.rng, sp.flock))));
    // Half the flocks are ALREADY settled when the viewer arrives (a wood has
    // birds in it before you walk in); the rest fly in from the edge.
    const P = this.perches;
    const settledPool = k === 'hawk' ? (P.giants.length ? P.giants : P.trees) : sp.forages && this.rng() < 0.4 ? P.ground : P.trees.length ? P.trees : P.roofs;
    if (this.rng() < 0.5 && settledPool.length) {
      const far = settledPool.filter((t) => Math.hypot(t.x - px, t.z - pz) > 25);
      const pool = far.length ? far : settledPool;
      const t = pool[Math.floor(this.rng() * pool.length)];
      const leader = makeBird(this.nextId++, k, t.x, t.y, t.z, this.rng);
      leader.state = landedState(t.kind); leader.target = { ...t }; leader.timer = dwellFor(leader.state, this.rng); leader.vx = leader.vy = leader.vz = 0;
      this.birds.push(leader);
      for (let i = 1; i < size; i++) {
        const near = t.kind === 'tree' ? P.trees.filter((o) => Math.hypot(o.x - t.x, o.z - t.z) < 25 && o !== t) : [];
        const at = near.length ? near[Math.floor(this.rng() * near.length)] : { x: t.x + (this.rng() - 0.5) * 4, y: t.kind === 'ground' ? this.groundAt(t.x, t.z) : t.y, z: t.z + (this.rng() - 0.5) * 4, kind: t.kind };
        const f = makeBird(this.nextId++, k, at.x, at.y, at.z, this.rng);
        f.flock = leader.id; f.slot = i; f.state = landedState(at.kind); f.target = { ...at }; f.timer = dwellFor(f.state, this.rng); f.vx = f.vy = f.vz = 0;
        this.birds.push(f);
      }
      return;
    }
    const a = this.rng() * Math.PI * 2, d = 95 + this.rng() * 45;
    const x = px + Math.cos(a) * d, z = pz + Math.sin(a) * d;
    const y = this.flightFloor(x, z) + randRange(this.rng, sp.altitude);
    const leader = makeBird(this.nextId++, k, x, y, z, this.rng);
    leader.yaw = Math.atan2(-(px - x), -(pz - z));
    this.birds.push(leader);
    for (let i = 1; i < size; i++) {
      const s = flockSlot(x, z, leader.yaw, i, sp.wingspan);
      const f = makeBird(this.nextId++, k, s.x, y + (this.rng() - 0.5) * 2, s.z, this.rng);
      f.flock = leader.id; f.slot = i; f.yaw = leader.yaw;
      this.birds.push(f);
    }
  }

  /** A flock riding the sky around an airborne viewer: air targets at the viewer's height, a leader and its V. */
  private spawnAloft(k: BirdSpeciesKey, n: number, px: number, pz: number): void {
    const sp = BIRD_SPECIES[k];
    const size = Math.max(1, Math.min(n, Math.round(randRange(this.rng, sp.flock))));
    const a = this.rng() * Math.PI * 2, d = 120 + this.rng() * 80;
    const x = px + Math.cos(a) * d, z = pz + Math.sin(a) * d;
    const y = atlas.localY + (this.rng() - 0.5) * 50;
    const leader = makeBird(this.nextId++, k, x, y, z, this.rng);
    leader.aloft = true; leader.circuits = 99; leader.yaw = Math.atan2(-(px - x), -(pz - z));
    this.birds.push(leader);
    for (let i = 1; i < size; i++) {
      const s = flockSlot(x, z, leader.yaw, i, sp.wingspan);
      const f = makeBird(this.nextId++, k, s.x, y + (this.rng() - 0.5) * 3, s.z, this.rng);
      f.flock = leader.id; f.slot = i; f.yaw = leader.yaw; f.aloft = true;
      this.birds.push(f);
    }
  }

  /** Probe lever: a settled bird `dist` m from the viewer (to walk up to). */
  private spawnNear(species: BirdSpeciesKey, dist: number, state: 'ground' | 'perch'): number {
    const a = this.rng() * Math.PI * 2;
    const x = atlas.localX + Math.cos(a) * dist, z = atlas.localZ + Math.sin(a) * dist;
    const y = this.groundAt(x, z) + (state === 'perch' ? 3 : 0);
    const b = makeBird(this.nextId++, species, x, y, z, this.rng);
    b.state = state; b.timer = 60; b.target = { x, y, z, kind: state === 'perch' ? 'tree' : 'ground' }; b.vx = b.vy = b.vz = 0;
    this.birds.push(b);
    return b.id;
  }

  // ─── one bird ────────────────────────────────────────────────────────────
  private stepBird(b: Bird, dt: number, px: number, pz: number): void {
    const sp = BIRD_SPECIES[b.species];
    const settled = b.state === 'perch' || b.state === 'ground' || b.state === 'drink';
    if (settled) {
      b.timer -= dt;
      // A person too close flushes it (every threat refresh, staggered by id).
      if (shouldFlee(sp, this.nearestThreat(b.x, b.z))) {
        let tx = px, tz = pz, best = Infinity;
        for (const t of this.threats) { const d = Math.hypot(t.x - b.x, t.z - b.z); if (d < best) { best = d; tx = t.x; tz = t.z; } }
        b.state = 'flee'; b.target = fleeTarget(b, tx, tz, 6 + sp.wingspan * 4); b.timer = FLEE_BURST_S;
        this.stats.flees++;
        const dv = Math.hypot(b.x - px, b.z - pz);
        if (dv < 30 && performance.now() - this.lastFlutterMs > 400) { this.lastFlutterMs = performance.now(); sfxAt('sfx_bird_flutter', b, { gain: 0.35 * (1 - dv / 30), cooldownMs: 300, caption: 'Wings' }); }
        return;
      }
      if (b.state === 'ground') {
        const h = groundHop(b, dt, this.rng);
        if (h.dx || h.dz) { b.x += h.dx; b.z += h.dz; b.y = this.groundAt(b.x, b.z); }
      } else if (b.state === 'drink') {
        b.peck += dt; // the dip cadence (the renderer reads it)
      }
      this.maybeCall(b, sp, px, pz, dt);
      if (b.timer <= 0) {
        b.state = 'fly'; b.target = null; b.vy = 1.5;
        // Four times in ten a bird leaving its perch takes a circuit in the
        // air first (the sky has birds in it, not only the branches).
        if (this.rng() < 0.4) b.circuits = 1 + Math.floor(this.rng() * 2);
      }
      return;
    }
    if (b.state === 'flee') {
      const t = b.target!;
      steerToward(b, t.x, t.y, t.z, sp.cruise * FLEE_SPEED_MULT, dt, 3.2, 6);
      this.integrate(b, dt);
      b.timer -= dt;
      if (b.timer <= 0) { b.state = 'fly'; b.target = null; }
      return;
    }
    if (b.state === 'soar') {
      const gy = this.groundAt(b.x, b.z);
      const t = soarTarget(b, sp.altitude[1], gy);
      steerToward(b, t.x, t.y, t.z, sp.cruise * 0.85, dt, 1.2, 2);
      this.integrate(b, dt);
      b.timer -= dt;
      this.maybeCall(b, sp, px, pz, dt);
      if (b.timer <= 0) { b.state = 'fly'; b.target = null; }
      return;
    }
    // ── fly ──
    const leader = b.flock !== b.id ? (this.byId.get(b.flock) ?? null) : null;
    if (leader && leader.state === 'fly' && leader.target && leader.target.kind === 'air') {
      // Follow the leader's slot while it wanders.
      const s = flockSlot(leader.x, leader.z, leader.yaw, b.slot, sp.wingspan);
      steerToward(b, s.x, leader.y + Math.sin(b.id) * 0.6, s.z, sp.cruise * (Math.hypot(s.x - b.x, s.z - b.z) > 6 ? 1.15 : 1), dt);
      this.integrate(b, dt);
      return;
    }
    if (!b.target) {
      if ((b.circuits ?? 0) > 0) {
        // An air circuit: a waypoint 40–110 m off at the band (higher when the viewer is aloft).
        b.circuits!--;
        const a = this.rng() * Math.PI * 2, d = 40 + this.rng() * 70;
        const x = b.x + Math.cos(a) * d, z = b.z + Math.sin(a) * d;
        const alt = this.viewerAgl > ALOFT_AGL_M ? this.viewerAgl + (this.rng() - 0.5) * 60 : randRange(this.rng, sp.altitude);
        b.target = { x, y: this.flightFloor(x, z) + Math.max(4, alt), z, kind: 'air' };
        b.timer = 8 + this.rng() * 10;
      } else b.target = this.chooseTarget(b, sp, leader);
      if (b.target.kind === 'air' && sp.soars && this.rng() < 0.6 && b.species === 'hawk') {
        b.state = 'soar'; b.orbitX = px + (this.rng() - 0.5) * 120; b.orbitZ = pz + (this.rng() - 0.5) * 120; b.orbitR = 40 + this.rng() * 40; b.timer = dwellFor('soar', this.rng);
        return;
      }
      if (b.target.kind === 'air') b.timer = 8 + this.rng() * 10;
    }
    const t = b.target;
    const dist = Math.hypot(t.x - b.x, t.z - b.z, t.y - b.y);
    if (t.kind === 'air') {
      steerToward(b, t.x, t.y, t.z, sp.cruise, dt);
      this.integrate(b, dt);
      b.timer -= dt;
      if (dist < 4 || b.timer <= 0) { b.target = null; if (b.aloft && (b.circuits ?? 0) <= 0) b.circuits = 99; }
      return;
    }
    // A landing approach: aim a little above the perch, slow in, then settle.
    const approachY = dist > ARRIVE_M * 4 ? t.y + Math.min(3, dist * 0.35) : t.y;
    steerToward(b, t.x, approachY, t.z, arriveSpeed(dist, sp.cruise), dt, dist < 12 ? 3.5 : 2.4, 5);
    this.integrate(b, dt, dist < 6);
    if (dist < ARRIVE_M) {
      b.x = t.x; b.y = t.y; b.z = t.z; b.vx = b.vy = b.vz = 0;
      b.state = landedState(t.kind);
      b.timer = dwellFor(b.state, this.rng);
      b.peck = 0;
      this.stats.landings[t.kind] = (this.stats.landings[t.kind] ?? 0) + 1;
    }
  }

  private integrate(b: Bird, dt: number, landing = false): void {
    b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
    b.flap += flapAdvance(b, dt);
    if (!landing) {
      const floor = this.flightFloor(b.x, b.z) + 1.5;
      if (b.y < floor) { b.y = floor; if (b.vy < 0) b.vy = 0; }
    }
  }

  private chooseTarget(b: Bird, sp: (typeof BIRD_SPECIES)[BirdSpeciesKey], leader: Bird | null): Target {
    const P = this.perches;
    // A follower joins its leader where it settled (beside it).
    if (leader && leader.target && leader.target.kind !== 'air' && (leader.state !== 'fly')) {
      const lt = leader.target;
      if (lt.kind === 'ground' || lt.kind === 'shore' || lt.kind === 'roof' || lt.kind === 'ship') {
        const jx = (this.rng() - 0.5) * 4, jz = (this.rng() - 0.5) * 4;
        return { x: lt.x + jx, y: lt.kind === 'ground' || lt.kind === 'shore' ? this.groundAt(lt.x + jx, lt.z + jz) : lt.y, z: lt.z + jz, kind: lt.kind };
      }
      const near = P.trees.filter((t) => Math.hypot(t.x - lt.x, t.z - lt.z) < 25);
      if (near.length) return near[Math.floor(this.rng() * near.length)];
    }
    const avail: Partial<Record<PerchKind, number>> = {
      tree: (b.species === 'hawk' ? P.giants.length + P.trees.length : P.trees.length), roof: P.roofs.length, ship: P.ships.length, ground: P.ground.length, shore: P.shores.length,
    };
    const kind = pickPerchKind(sp, avail, this.night, this.rng);
    const pool = kind === 'tree' ? (b.species === 'hawk' && P.giants.length ? P.giants : P.trees) : kind === 'roof' ? P.roofs : kind === 'ship' ? P.ships : kind === 'ground' ? P.ground : kind === 'shore' ? P.shores : [];
    // Prefer perches some way off (a bird rarely re-lands where it stood) inside the range.
    const candidates = pool.filter((t) => Math.hypot(t.x - b.x, t.z - b.z) > 12);
    const pick = (candidates.length ? candidates : pool)[Math.floor(this.rng() * Math.max(1, (candidates.length ? candidates : pool).length))];
    if (pick) return { ...pick };
    // Nothing to land on: wander at the band.
    const a = this.rng() * Math.PI * 2, d = 40 + this.rng() * 60;
    const x = b.x + Math.cos(a) * d, z = b.z + Math.sin(a) * d;
    return { x, y: this.flightFloor(x, z) + randRange(this.rng, sp.altitude), z, kind: 'air' };
  }

  private lastCallMs = 0;
  private maybeCall(b: Bird, sp: (typeof BIRD_SPECIES)[BirdSpeciesKey], px: number, pz: number, dt: number): void {
    b.callIn -= dt;
    if (b.callIn > 0) return;
    // "Only once in a while, if a bird is near you": each bird calls a few
    // times a minute at most, only inside CALL_RANGE_M, and never over
    // another bird's call (one voice every CALL_GAP_S across the flock).
    b.callIn = randRange(this.rng, sp.callEvery) * 3;
    const d = Math.hypot(b.x - px, b.z - pz);
    if (d > CALL_RANGE_M) return;
    const now = performance.now();
    if (now - this.lastCallMs < CALL_GAP_S * 1000) return;
    this.lastCallMs = now;
    sfxAt(sp.call, b, { gain: 0.3 * (1 - d / CALL_RANGE_M), rate: 0.94 + this.rng() * 0.12, cooldownMs: 250, caption: 'Birdsong' });
    this.stats.calls++;
  }

  // ─── skeins ──────────────────────────────────────────────────────────────
  private stepSkeins(dt: number, px: number, pz: number): void {
    this.skeinIn -= this.viewerAgl > ALOFT_AGL_M ? dt * 2.5 : dt; // aloft, skeins pass more often
    if (this.skeinIn <= 0 && (BIRD_CAP[activeTier()] ?? 0) > 0) {
      this.skeinIn = randRange(this.rng, SKEIN_EVERY_S);
      const season = SEASONS[Math.floor(Date.now() / 604_800_000) % 4];
      this.skeins.push(planSkein(px, pz, this.flightFloor(px, pz), season, this.rng));
      this.stats.skeins++;
      void sfxAvailable('sfx_bird_flock'); // an ask STARTS the load — the pass is half a crossing away, so it is decoded by then
    }
    for (const s of this.skeins) {
      const before = s.t; s.t += dt;
      if (before < s.life * 0.5 && s.t >= s.life * 0.5) {
        // A passing skein: the recorded flock pass when it is installed — else a rush of wingbeats built from the
        // flutter clip layered at two pitches (what played while the clip did not exist) — then the species' call.
        const lead = skeinSlot(s, 0, BIRD_SPECIES[s.species].wingspan);
        const at = { x: lead.x, y: lead.y, z: lead.z };
        if (sfxAvailable('sfx_bird_flock') === true) sfxAt('sfx_bird_flock', at, { gain: 0.34, rate: 0.96 + this.rng() * 0.08, cooldownMs: 200, occlude: false, caption: 'Flock overhead' });
        else {
          sfxAt('sfx_bird_flutter', at, { gain: 0.22, rate: 0.85, cooldownMs: 200, occlude: false, caption: 'Flock overhead' });
          sfxLater(260, () => sfxAt('sfx_bird_flutter', at, { gain: 0.18, rate: 0.95, cooldownMs: 100, occlude: false }));
        }
        sfxLater(520, () => sfxAt(BIRD_SPECIES[s.species].call, at, { gain: 0.2, rate: 1.05, cooldownMs: 100, occlude: false }));
        this.stats.flockSounds++;
      }
    }
    let expired = false;
    for (const s of this.skeins) if (s.t >= s.life) { expired = true; break; }
    if (expired) this.skeins = this.skeins.filter((s) => s.t < s.life);
  }

  // ─── bodies ──────────────────────────────────────────────────────────────
  private modelKey(k: BirdSpeciesKey, pose: Pose): string { return MODEL_KEYS[k][pose]; }

  private ensureModels(): void {
    for (const k of BIRD_SPECIES_KEYS) for (const pose of ['fly', 'perch'] as Pose[]) {
      const key = this.modelKey(k, pose);
      const cur = this.models.get(key);
      if (cur?.source === 'glb') continue;
      if (!cur) this.models.set(key, this.buildPlaceholder(k, pose));
      void this.tryLoadGlb(k, pose);
    }
  }

  private catalogId(prefabKey: string): bigint | null {
    const conn = atlas.conn;
    if (!conn) return null;
    try { for (const pm of (conn.db.prefabModel as unknown as { iter: () => Iterable<{ prefabKey: string; assetId: bigint }> }).iter()) if (pm.prefabKey === prefabKey) return pm.assetId; } catch { /* churn */ }
    return null;
  }

  private loading = new Set<string>();
  private async tryLoadGlb(k: BirdSpeciesKey, pose: Pose): Promise<void> {
    const key = this.modelKey(k, pose);
    if (this.loading.has(key) || !this.assets) return;
    const id = this.catalogId(`prop:bird_${k}_${pose}`);
    if (id === null) return;
    this.loading.add(key);
    try {
      // LOD2 (15 % of the 60k-triangle ingest ≈ 9k) for a 30 cm bird: the
      // triangle census showed the 22 birds in view at LOD1 costing 852k
      // triangles — second only to the whole instanced prop set (2026-09-12).
      const { object, release } = await this.assets.loadRepresentation(id, 2);
      if (this.disposed) { release(); return; } // a load that completes after dispose() must not re-add a mesh
      let src: THREE.Mesh | null = null;
      object.traverse((o) => { if (!src && (o as THREE.Mesh).isMesh) src = o as THREE.Mesh; });
      if (!src) { release(); return; }
      const mesh = src as THREE.Mesh;
      const geo = mesh.geometry.clone();
      geo.applyMatrix4(mesh.matrixWorld);
      geo.computeBoundingBox();
      const size0 = new THREE.Vector3(); geo.boundingBox!.getSize(size0);
      // ORIENTATION LAW (render-glb-views, 2026-09-11): Meshy models the FLYING
      // bird UPRIGHT — head +Y, belly +Z, wings ±X — and the perched bird
      // standing, facing +Z. Lay the flyer flat (head −Z, belly down) and turn
      // the stander half round; every bird then faces −Z, the vehicle yaw.
      // Looked at (render-glb-views, 2026-09-11): the jay and the starling
      // flyers are modelled UPRIGHT, the gull and the hawk HORIZONTAL (banked,
      // wings raised — as tall as an upright bird, so the box can't tell).
      const upright = FLY_UPRIGHT[k] ?? AtlasFaunaModule.isUprightFlyer(geo, size0);
      if (pose === 'fly' && upright) geo.rotateX(Math.PI / 2);
      geo.rotateY(Math.PI);
      geo.computeBoundingBox();
      const bb = geo.boundingBox!;
      const size = new THREE.Vector3(); bb.getSize(size);
      const centre = new THREE.Vector3(); bb.getCenter(centre);
      geo.translate(-centre.x, pose === 'perch' ? -bb.min.y : -centre.y, -centre.z);
      const axisZ = pose === 'fly' && size.z > size.x * 1.5 ? 1 : 0;
      // The flyer scales by its wingspan (x); the stander by its length (z ≈ 0.7 of a wingspan).
      const half = pose === 'fly' ? Math.max(size.x, size.z) * 0.5 : size.z * 0.5 / 0.7;
      const srcMat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
      const model = this.makeModel(k, pose, geo, this.nodeMaterialFrom(srcMat, pose === 'fly', half, axisZ), half, 0, 0, 'glb', release);
      const old = this.models.get(key);
      this.models.set(key, model);
      if (old) this.disposeModel(old);
    } catch { /* keep the stand-in; retried on the next sweep */ }
    finally { this.loading.delete(key); }
  }

  /**
   * An UPRIGHT flyer (the jay: head +Y, belly +Z) has its central body slab
   * taller than it is long; a horizontal one (the gull, the hawk — banked,
   * wings raised) has the body running along Z. The bounding box alone can't
   * tell them apart: raised wings make a horizontal bird as tall as an
   * upright one.
   */
  private static isUprightFlyer(geo: THREE.BufferGeometry, size: THREE.Vector3): boolean {
    const pos = geo.getAttribute('position');
    if (!pos) return size.y > size.z * 1.1;
    const bb = geo.boundingBox!;
    const cx = (bb.min.x + bb.max.x) * 0.5;
    const slab = Math.max(size.x, size.z) * 0.1;
    let ymin = Infinity, ymax = -Infinity, zmin = Infinity, zmax = -Infinity, n = 0;
    for (let i = 0; i < pos.count; i += 3) {
      if (Math.abs(pos.getX(i) - cx) > slab) continue;
      const y = pos.getY(i), z = pos.getZ(i);
      if (y < ymin) ymin = y; if (y > ymax) ymax = y; if (z < zmin) zmin = z; if (z > zmax) zmax = z; n++;
    }
    if (n < 30) return size.y > size.z * 1.1;
    return ymax - ymin > (zmax - zmin) * 1.15;
  }

  private nodeMaterialFrom(src: THREE.MeshStandardMaterial, flying: boolean, half: number, axisZ: 0 | 1): THREE.MeshStandardNodeMaterial {
    const mat = new THREE.MeshStandardNodeMaterial();
    mat.map = src.map ?? null; mat.normalMap = src.normalMap ?? null; mat.roughnessMap = src.roughnessMap ?? null; mat.metalnessMap = src.metalnessMap ?? null;
    mat.color.copy(src.color ?? new THREE.Color(1, 1, 1)); mat.roughness = src.roughness ?? 0.8; mat.metalness = src.metalness ?? 0;
    mat.side = THREE.DoubleSide;
    if (flying) this.applyFlap(mat, half, axisZ);
    return mat;
  }

  /** The wing hinge: past `hinge` along the wing axis the tips rise and fall with the phase (and the previous phase for the blur). */
  private applyFlap(mat: THREE.MeshStandardNodeMaterial, half: number, axisZ: 0 | 1): void {
    const hinge = half * 0.16;
    const aPair = attribute('aFlap', 'vec2');
    const aFlap = aPair.x;
    const aPrev = aPair.y;
    const lift = (phase: ReturnType<typeof float>) => {
      const p = positionLocal;
      // MEASURE in geometry space, DISPLACE in local space. WHY (2026-09-14):
      // three applies instancedMesh() to positionLocal BEFORE it builds
      // positionNode (NodeMaterial.setupPosition), so inside here
      // `positionLocal` is already the instance-transformed position — an
      // absolute scene coordinate, not the model's wing axis. `|positionLocal.x|`
      // against a model-space hinge was comparing a bird's world X against a
      // few centimetres, so every vertex read as "past the hinge" by an
      // enormous margin and the hinge did nothing it was meant to do.
      // `positionGeometry` is the raw position attribute — three's own
      // documented way to reach the pre-transform local position.
      const g = positionGeometry;
      const wing = axisZ ? g.z : g.x;
      const reach = max(abs(wing).sub(hinge), 0);
      const k = reach.div(Math.max(1e-3, half - hinge));
      const dy = sin(phase).mul(k).mul(k.mul(0.5).add(0.5)).mul(half * 0.6);
      return vec3(p.x, p.y.add(dy), p.z);
    };
    // r185/WebGPU LAW (night_live.ts): an assign() needs an enclosing Fn()
    // stack — outside it the WGSL pipeline descriptor comes out incomplete
    // and the FLYING body never drew on the user's WebGPU: a flushed bird
    // simply vanished ("they just disappear", 2026-09-11).
    mat.positionNode = Fn(() => {
      positionPrevious.assign(lift(aPrev as never));
      return lift(aFlap as never);
    })();
  }

  private makeModel(k: BirdSpeciesKey, pose: Pose, geo: THREE.BufferGeometry, mat: THREE.Material, unit: number, feet: number, yawOff: number, source: 'placeholder' | 'glb', release: (() => void) | null): BirdModel {
    const cap = BIRD_MAX + 2 * SKEIN_SIZE[1]; // the flock AND two overlapping skeins (a 64 cap cut the second V's tail)
    // Strip what the bird shader never reads (a cooked GLB carries tangents,
    // second uvs, colours): every attribute is a vertex buffer on WebGPU.
    for (const name of ['tangent', 'uv1', 'uv2', 'uv3', 'color', 'skinIndex', 'skinWeight']) if (geo.getAttribute(name)) geo.deleteAttribute(name);
    const flap = new THREE.InstancedBufferAttribute(new Float32Array(cap * 2), 2);
    flap.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aFlap', flap);
    const mesh = new THREE.InstancedMesh(geo, mat, cap);
    mesh.count = 0; mesh.frustumCulled = false; mesh.castShadow = false; mesh.name = `birds:${k}:${pose}`;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.scene.add(mesh);
    void source;
    return { mesh, flap, unit, yawOff, feet, source, release };
  }

  private buildPlaceholder(k: BirdSpeciesKey, pose: Pose): BirdModel {
    const sp = BIRD_SPECIES[k];
    const span = sp.wingspan, bodyL = span * 0.42, bodyR = span * 0.09;
    const parts: THREE.BufferGeometry[] = [];
    const body = new THREE.SphereGeometry(1, 10, 8); body.scale(bodyR, bodyR * 0.9, bodyL * 0.5); parts.push(body);
    const head = new THREE.SphereGeometry(bodyR * 0.75, 8, 6); head.translate(0, bodyR * 0.6, -bodyL * 0.5); parts.push(head);
    const beak = new THREE.ConeGeometry(bodyR * 0.3, bodyR * 0.9, 6); beak.rotateX(-Math.PI / 2); beak.translate(0, bodyR * 0.5, -bodyL * 0.5 - bodyR * 0.9); parts.push(beak);
    const tail = new THREE.PlaneGeometry(bodyR * 2.2, bodyL * 0.45); tail.rotateX(-Math.PI / 2); tail.translate(0, 0, bodyL * 0.55); parts.push(tail);
    if (pose === 'fly') {
      for (const s of [1, -1]) { const w = new THREE.PlaneGeometry(span * 0.46, bodyL * 0.55); w.rotateX(-Math.PI / 2); w.translate(s * (span * 0.23 + bodyR * 0.5), bodyR * 0.2, -bodyL * 0.05); parts.push(w); }
    } else {
      for (const s of [1, -1]) { const w = new THREE.BoxGeometry(bodyR * 0.35, bodyR * 0.9, bodyL * 0.8); w.translate(s * bodyR * 1.05, bodyR * 0.15, bodyL * 0.05); parts.push(w); }
      for (const s of [1, -1]) { const leg = new THREE.CylinderGeometry(bodyR * 0.08, bodyR * 0.08, bodyR * 1.2, 5); leg.translate(s * bodyR * 0.4, -bodyR * 1.3, 0); parts.push(leg); }
    }
    const merged = mergeGeometries(parts.map((p) => p.toNonIndexed()), false)!;
    merged.computeVertexNormals();
    if (pose === 'perch') { merged.computeBoundingBox(); merged.translate(0, -merged.boundingBox!.min.y, 0); }
    const tint: Record<BirdSpeciesKey, number> = { jay: 0x2f5fc4, gull: 0xe8ecf0, hawk: 0xa8622a, starling: 0x1c1d22 };
    const mat = new THREE.MeshStandardNodeMaterial({ color: tint[k], roughness: 0.85, side: THREE.DoubleSide });
    if (pose === 'fly') this.applyFlap(mat, span * 0.5, 0);
    return this.makeModel(k, pose, merged, mat, span * 0.5, 0, 0, 'placeholder', null);
  }

  private disposeModel(m: BirdModel): void {
    this.scene.remove(m.mesh);
    deferDisposeTree(m.mesh);
    m.release?.();
  }

  private putInstance(key: string, x: number, y: number, z: number, yaw: number, pitch: number, bank: number, span: number, flap: number, flapPrev: number): void {
    const m = this.models.get(key);
    if (!m) return;
    const i = this.counts.get(key) ?? 0;
    if (i >= m.mesh.instanceMatrix.count) return;
    this._e.set(pitch, yaw + m.yawOff, bank, 'YXZ');
    this._q.setFromEuler(this._e);
    const s = span / Math.max(1e-3, m.unit * 2);
    this._s.set(s, s, s);
    this._p.set(x, y, z);
    this._m.compose(this._p, this._q, this._s);
    m.mesh.setMatrixAt(i, this._m);
    m.flap.setXY(i, flap, flapPrev);
    this.counts.set(key, i + 1);
  }

  private renderBirds(): void {
    const counts = this.counts;
    counts.clear();
    for (const b of this.birds) {
      const sp = BIRD_SPECIES[b.species];
      const settled = b.state === 'perch' || b.state === 'ground' || b.state === 'drink';
      if (settled) {
        const dip = b.state === 'drink' ? (Math.sin(b.peck * 2.2) > 0.6 ? -0.55 : 0) : b.state === 'ground' && b.peck > 0.4 && b.peck < 0.8 ? -0.5 : 0;
        this.putInstance(MODEL_KEYS[b.species].perch, b.x, b.y, b.z, b.yaw, dip, 0, sp.wingspan, 0, 0);
      } else {
        const hs = Math.hypot(b.vx, b.vz) || 1;
        const pitch = Math.atan2(b.vy, hs) * 0.5;
        this.putInstance(MODEL_KEYS[b.species].fly, b.x, b.y, b.z, b.yaw, pitch, 0, sp.wingspan, b.flap, b.flap - flapAdvance(b, 1 / 60));
      }
    }
    for (const s of this.skeins) {
      const sp = BIRD_SPECIES[s.species];
      const key = MODEL_KEYS[s.species].fly;
      for (let i = 0; i < s.count; i++) {
        const p = skeinSlot(s, i, sp.wingspan);
        this.putInstance(key, p.x, p.y, p.z, s.yaw, 0, 0, sp.wingspan, p.flap, p.flap - sp.flapHz * Math.PI * 2 / 60);
      }
    }
    for (const [key, m] of this.models) {
      const n = counts.get(key) ?? 0;
      m.mesh.count = n;
      m.mesh.visible = n > 0;
      if (n > 0) { m.mesh.instanceMatrix.needsUpdate = true; m.flap.needsUpdate = true; }
    }
  }

  private clearBirds(): void { this.birds = []; this.skeins = []; }

  // ─── bugs ────────────────────────────────────────────────────────────────
  private ensureBugMesh(): THREE.InstancedMesh {
    if (this.bugMesh) return this.bugMesh;
    const cap = 128;
    const geo = new THREE.PlaneGeometry(1, 1);
    const glow = new THREE.InstancedBufferAttribute(new Float32Array(cap).fill(1), 1);
    glow.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aGlow', glow);
    const mat = new THREE.MeshBasicNodeMaterial();
    mat.transparent = true; mat.depthWrite = false; mat.side = THREE.DoubleSide;
    markMrtOverlay(mat); // bug sprites over the woods
    const aGlow = attribute('aGlow', 'float');
    const radial = float(1).sub(smoothstep(0.18, 0.5, length(uv().sub(0.5))));
    mat.opacityNode = radial.mul(mix(float(0.85), aGlow as never, this.uBugEmissive as never));
    mat.colorNode = color(this.uBugColor as never).mul(float(1).add((this.uBugEmissive as never as ReturnType<typeof float>).mul(aGlow as never).mul(2.5)));
    const mesh = new THREE.InstancedMesh(geo, mat, cap);
    mesh.count = 0; mesh.frustumCulled = false; mesh.name = 'bugs'; mesh.renderOrder = 4;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.scene.add(mesh);
    this.bugMesh = mesh; this.bugGlow = glow;
    return mesh;
  }

  private pushPerson(i: number, x: number, y: number, z: number, still: number): void {
    const p = this.people[i];
    if (p) { p.x = x; p.y = y; p.z = z; p.still = still; } else this.people[i] = { x, y, z, still };
  }

  private stepBugs(dt: number, subs: number, px: number, py: number, pz: number): void {
    if ((activePreset().particleCap ?? 0) <= 0) { this.clearBugs(); return; }
    const moved = Math.hypot(px - this.lastPx, pz - this.lastPz);
    this.stillFor = moved > 0.05 ? 0 : this.stillFor + dt;
    this.lastPx = px; this.lastPz = pz;
    // The weather's TARGET (immediate), not its lerped live state: the swarm
    // must not wait minutes for the last drops of a stopped rain.
    const w = this.weather?.wind();
    const precip = w ? (w.targetKind === 'rain' || w.targetKind === 'snow' ? w.targetDensity : 0) : 0;
    const temp = w?.temperatureC ?? 18;
    const density = forestDensity(this.seed, px, pz, this.sea);
    const kind = swarmAllowed(density, precip, atlas.interiorId !== null, temp) ? bugKindFor(this.night, temp) : null;
    if (!kind) { this.clearBugs(); return; }
    const rules = BUG_RULES[kind];
    const mesh = this.ensureBugMesh();
    if (kind !== this.bugKind) {
      this.bugKind = kind;
      this.bugs = [];
      this.swarmX = px; this.swarmZ = pz;
      const tint: Record<BugKind, number> = { gnat: 0x2a2620, moth: 0xd9c9a3, firefly: 0xd4ff5a };
      (this.uBugColor as unknown as { value: THREE.Color }).value.set(tint[kind]);
      (this.uBugEmissive as unknown as { value: number }).value = kind === 'firefly' ? 1 : 0;
      const mat = mesh.material as THREE.Material;
      const wantBlend = kind === 'firefly' ? THREE.AdditiveBlending : THREE.NormalBlending;
      if (mat.blending !== wantBlend) { mat.blending = wantBlend; mat.needsUpdate = true; }
    }
    const gy = this.groundAt(this.swarmX, this.swarmZ);
    const cy = gy + rules.height;
    while (this.bugs.length < rules.count) {
      const a = this.rng() * Math.PI * 2, d = this.rng() * rules.radius;
      this.bugs.push({ x: this.swarmX + Math.cos(a) * d, y: cy + (this.rng() - 0.5) * 1.6, z: this.swarmZ + Math.sin(a) * d, vx: 0, vy: 0, vz: 0, phase: this.rng() * 10, size: rules.size[0] + this.rng() * (rules.size[1] - rules.size[0]) });
    }
    if (this.bugs.length > rules.count) this.bugs.length = rules.count;
    const wind = windVector;
    const c = swarmCentreStep(this.swarmX, this.swarmZ, px, pz, wind.x, wind.y, wind.z, dt);
    this.swarmX = c.x; this.swarmZ = c.z;
    const people = this.people;
    let np = 0;
    this.pushPerson(np++, px, py, pz, this.stillFor);
    for (const t of this.threats) if (t.x !== px && Math.hypot(t.x - px, t.z - pz) < 14) this.pushPerson(np++, t.x, t.y, t.z, 0);
    people.length = np;
    let nearHead = 0;
    const cam = this.app?.renderer.cameras.main as THREE.Camera | undefined;
    // WORLD orientation: the camera is a child of the yawed avatar rig, so its
    // local quaternion carries the pitch alone — the quads faced world +Z and
    // the swarm vanished edge-on whenever the player looked along ±X (2026-09-12).
    if (cam) cam.getWorldQuaternion(this._camQ);
    const sdt = dt / subs;
    for (let i = 0; i < this.bugs.length; i++) {
      const b = this.bugs[i];
      for (let s = 0; s < subs; s++) stepBug(b, rules, kind, this.swarmX, cy, this.swarmZ, people, wind.x, wind.y, wind.z, sdt, this.rng);
      const floor = this.groundAt(b.x, b.z) + 0.25;
      if (b.y < floor) { b.y = floor; b.vy = Math.abs(b.vy) * 0.5; }
      if (Math.hypot(b.x - px, b.y - (py + 1.5), b.z - pz) < 2.2) nearHead++;
      const flutter = kind === 'moth' ? 1 + 0.25 * Math.sin(b.phase * 18) : 1;
      this._p.set(b.x, b.y, b.z);
      this._s.set(b.size * flutter, b.size, 1);
      this._m.compose(this._p, this._camQ, this._s);
      mesh.setMatrixAt(i, this._m);
      this.bugGlow!.setX(i, kind === 'firefly' ? fireflyGlow(b.phase, rules.pulse) : 1);
    }
    mesh.count = this.bugs.length; mesh.visible = true;
    mesh.instanceMatrix.needsUpdate = true; this.bugGlow!.needsUpdate = true;
    this.bugNearHead = nearHead;
    sfxLoop('sfx_bugs_swarm', 'fauna').set(kind === 'gnat' ? Math.min(0.3, nearHead / 25) : 0, 1, 0.6);
  }

  private clearBugs(): void {
    if (this.bugs.length) { this.bugs = []; this.bugKind = null; sfxLoop('sfx_bugs_swarm', 'fauna').set(0, 1, 0.6); }
    if (this.bugMesh) { this.bugMesh.count = 0; this.bugMesh.visible = false; }
    this.bugNearHead = 0;
  }

  // ─── diagnostics ─────────────────────────────────────────────────────────
  private diag(): object {
    const px = atlas.localX, pz = atlas.localZ;
    const byState: Record<string, number> = {}; const bySpecies: Record<string, number> = {};
    let nearest: { species: string; state: string; dist: number; id: number } | null = null;
    for (const b of this.birds) {
      byState[b.state] = (byState[b.state] ?? 0) + 1; bySpecies[b.species] = (bySpecies[b.species] ?? 0) + 1;
      const d = Math.hypot(b.x - px, b.z - pz);
      if (!nearest || d < nearest.dist) nearest = { species: b.species, state: b.state, dist: Math.round(d * 10) / 10, id: b.id };
    }
    const models: Record<string, string> = {};
    for (const [k, m] of this.models) models[k] = m.source;
    return {
      birds: this.birds.length, byState, bySpecies, habitat: this.habitat, night: Math.round(this.night * 100) / 100,
      // The usual answer to "where are the birds?": `gate` is non-null, or the
      // tier's `cap` is small because auto-tune walked the preset down.
      gate: this.lastGate, tier: activeTier(), cap: BIRD_CAP[activeTier()] ?? BIRD_MAX,
      range: { spawn: BIRD_RANGE_M, despawn: BIRD_DESPAWN_M, viewerAgl: Math.round(this.viewerAgl) },
      perches: { trees: this.perches.trees.length, giants: this.perches.giants.length, roofs: this.perches.roofs.length, ships: this.perches.ships.length, ground: this.perches.ground.length, shores: this.perches.shores.length },
      threats: this.threats.length, skeins: this.skeins.map((s) => ({ species: s.species, count: s.count, t: Math.round(s.t), life: Math.round(s.life), alt: Math.round(s.y) })),
      models, stats: { ...this.stats, landings: { ...this.stats.landings } }, nearest,
      bugs: { kind: this.bugKind, count: this.bugs.length, nearHead: this.bugNearHead, stillFor: Math.round(this.stillFor * 10) / 10 },
      bird: (id: number) => { const b = this.birds.find((o) => o.id === id); return b ? { ...b } : null; },
    };
  }

  dispose(): void {
    this.disposed = true;
    for (const [, m] of this.models) this.disposeModel(m);
    this.models.clear();
    if (this.bugMesh) { this.scene.remove(this.bugMesh); deferDisposeTree(this.bugMesh); this.bugMesh = null; }
    this.birds = []; this.bugs = [];
  }
}
